import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Input, Sheet, Typography } from '@mui/joy';
import { useInfiniteQuery } from '@tanstack/react-query';
import { OrganizationProfileAPI } from '../../../lib/apiOrganizationProfile';
import type { AccountUser } from '../../../types/admin';
export function AccountPeople({
  orgId,
  onEmail,
  onImpersonate,
}: {
  orgId: string;
  onEmail: (email: string) => void;
  onImpersonate: (user: AccountUser) => void;
}) {
  const [search, setSearch] = useState('');
  const query = useInfiniteQuery({
    queryKey: ['admin', 'account-users', orgId, search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      OrganizationProfileAPI.users(orgId, search, pageParam),
    getNextPageParam: (last, pages) =>
      last.next ? pages.length + 1 : undefined,
  });
  const users = query.data?.pages.flatMap((page) => page.results) || [];
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Box>
        <Typography level="h4">People at this account</Typography>
        <Typography level="body-sm">
          Email the right person or sign in as them to help. Inactive users are
          included and clearly marked.
        </Typography>
      </Box>
      <Input
        placeholder="Find a user by name or email"
        aria-label="Search account users"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {query.isPending && <Typography role="status">Loading users…</Typography>}
      {query.isError && (
        <Alert
          color="danger"
          endDecorator={<Button onClick={() => query.refetch()}>Retry</Button>}
        >
          Could not load users.
        </Alert>
      )}
      {!query.isPending && !query.isError && !users.length && (
        <Typography>No users match this search.</Typography>
      )}
      {users.map((user) => (
        <Sheet
          key={user.id}
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 'lg',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Typography level="title-md">{user.name}</Typography>
              <Chip size="sm" color={user.is_active ? 'success' : 'neutral'}>
                {user.is_active ? 'Active' : 'Inactive'}
              </Chip>
              <Chip size="sm" variant="outlined">
                {user.role}
              </Chip>
            </Box>
            <Typography
              level="body-sm"
              sx={{ overflowWrap: 'anywhere', mt: 0.5 }}
            >
              {user.email || 'No email'}
              {user.phone ? ` · ${user.phone}` : ''}
            </Typography>
            <Typography level="body-xs">
              {user.last_active
                ? `Last active ${new Date(user.last_active).toLocaleString()}`
                : 'Has not signed in'}
            </Typography>
          </Box>
          <Button
            variant="soft"
            disabled={!user.email}
            onClick={() => onEmail(user.email)}
          >
            Email
          </Button>
          <Button
            variant="outlined"
            disabled={!user.can_impersonate}
            onClick={() => onImpersonate(user)}
          >
            Sign in as user
          </Button>
        </Sheet>
      ))}
      {query.hasNextPage && (
        <Button
          variant="outlined"
          loading={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          Load more users ({users.length} of {query.data?.pages[0].count})
        </Button>
      )}
    </Box>
  );
}
